import { distinctUntilChanged } from 'rxjs/operators';
import { debounceTime, Subject, takeUntil, Observable } from 'rxjs';
import { L10N_LOCALE, L10nLocale, L10nTranslationService, L10nTranslatePipe } from 'angular-l10n';
import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, ChangeDetectionStrategy, ViewChildren, OnChanges, SimpleChanges, inject } from '@angular/core';
import { ReactiveFormsModule, Validators, FormBuilder, FormControl, FormArray, FormGroup, AbstractControl } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '../authentication/auth.service';
import { DateRange } from '../model/date.helper';
import { LogHelper } from '../logging/log-helper';
import { MatDialog } from '@angular/material/dialog';
import { CustomDateDialogComponent } from '../custom-date-dialog/custom-date-dialog.component';
import { MaxCharactersPipe } from '../pipes/max-characters/max-characters.pipe';
import { MaxItemsPipe } from '../pipes/max-items/max-items.pipe';
import { FilteredSearchOptionComponent } from './filtered-search-option/filtered-search-option.component';

export type FilterVariable = { value: `{{${string}}}`; label?: string; description?: string };
export type FilterDelimiter = '||' | ',';

export type FilterType = 'text' | 'date' | 'daterange' | 'daterange-menu' | 'boolean' | 'phone' | 'select' | 'tenant';
export type FilterValueType = string | number | boolean | Date | DateRange | string[] | null;
export type FilterFormValueType =
  | FormControl<string>
  | FormControl<number>
  | FormControl<boolean>
  | FormControl<Date>
  | FormGroup<{ start?: FormControl<Date>; end?: FormControl<Date> }>
  | AbstractControl<string>
  | null;

export type FilterValues = string[] | { value: unknown; label: string; icon?: string }[];
export type FilterValuesFunc = (field: string) => (input: FilterValueType) => Observable<FilterValues>;

export interface FilterField {
  field: string;
  localizedLabel: string;
  type: FilterType;
  values?: FilterValues;
  valuesFunc?: FilterValuesFunc;
  placeholder?: string;
  permissions?: string[];
  hide?: boolean;
  exclusiveWith?: string[];
  required?: boolean;
  variables?: FilterVariable[];
}

export interface Filter {
  field: string;
  value: FilterValueType;
  previous?: Partial<Filter>;
}

export interface FilterForm {
  field: FormControl<string | null>;
  value: FilterFormValueType;
}

@Component({
  selector: 'app-filtered-search',
  templateUrl: './filtered-search.component.html',
  styleUrls: ['./filtered-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FilteredSearchOptionComponent,
    NgTemplateOutlet,
    MatBadgeModule,
    MatButtonModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatTooltipModule,
    ReactiveFormsModule,
    L10nTranslatePipe,
    MaxCharactersPipe,
    MaxItemsPipe,
  ],
})
export class FilteredSearchComponent implements OnInit, OnDestroy, OnChanges {
  private readonly authService = inject(AuthService);
  private readonly fb = inject(FormBuilder);
  private readonly translate = inject(L10nTranslationService);
  private readonly dialog = inject(MatDialog);
  readonly locale = inject<L10nLocale>(L10N_LOCALE);

  @ViewChildren(MatMenuTrigger) menuTriggers: MatMenuTrigger[];
  @Input() set defaultSearch(value: string) {
    this.searchForm.setValue(value);
  }

  @Input() defaultFilters: Filter[];
  @Input() fields: FilterField[];
  @Input() hideSearch = false;
  @Input() searchLabel: string;
  @Input() constrainFilterMenu = false;
  @Input() type: 'button' | 'chip' = 'button';
  @Input() display: 'inline' | 'block' = 'block';

  @Output() filterChange = new EventEmitter<Filter>();
  @Output() filtersChange = new EventEmitter<Filter[]>();
  @Output() searchChange = new EventEmitter<string>();

  unappliedFilter = this.defaultFilterForm();

  appliedFilters: FormArray<FormGroup<FilterForm>> = this.fb.array([] as FormGroup<FilterForm>[]);
  searchForm: FormControl<string> = new FormControl(null);

  fieldDict: { [key: string]: FilterField } = {};

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.searchForm.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.searchChange.emit(value);
      });

    this.fieldDict = this.fields.reduce((dict, field) => {
      return { ...dict, [field.field]: field };
    }, {});
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fields) {
      this.fields = this.fields.filter((f) => this.authService.hasAllPermissions(f.permissions));

      this.fieldDict = this.fields.reduce((dict, field) => {
        return { ...dict, [field.field]: field };
      }, {});
    }

    if (changes.defaultFilters) {
      this.defaultFilters?.forEach((filter) => this.onApplyFilter(filter.field, filter.value));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  defaultFilterForm(): FormGroup<FilterForm> {
    return this.fb.group({
      field: [null, Validators.required],
      value: [null, Validators.required],
    }) as FormGroup<FilterForm>;
  }

  availableFilters(including?: Partial<Filter>): FilterField[] {
    if (!including) {
      return this.fields?.filter((f) => !this.isFilteredBy(f.field) && !f.hide);
    }

    return this.fields?.filter((f) => (!this.isFilteredBy(f.field) || including.field === f.field) && !f.hide);
  }

  isFilteredBy(field: string): boolean {
    return this.appliedFilters.value.some((f) => f.field === field);
  }

  getLocalizedLabel(field: string): string {
    if (!this.fieldDict[field]?.localizedLabel) {
      return field;
    }

    try {
      return this.translate.translate(this.fieldDict[field]?.localizedLabel, this.locale.language);
    } catch {
      LogHelper.debug('FilteredSearchComponent', `No translation found or unable to translate for ${field}`);
      return field;
    }
  }

  closeCurrentMenu() {
    for (const menu of this.menuTriggers ?? []) {
      menu?.closeMenu();
    }
  }

  canRemove(field: string) {
    const filter = this.fields.find((x) => x.field === field);
    return filter ? !filter.required : true;
  }

  onRemoveFilter(field: string) {
    const removedFilter = this.appliedFilters?.value?.find((f) => f.field === field);
    this.appliedFilters = this.fb.array(this.appliedFilters?.controls?.filter((f) => f.value.field !== field));

    this.filterChange.emit({ field: removedFilter?.field, value: null });
    this.filtersChange.emit(this.appliedFilters.getRawValue());
    LogHelper.debug('FilteredSearchComponent', `Removed filter: ${field}`);
  }

  onUpdateFilter(filter: Partial<Filter>) {
    if (filter.previous) {
      this.filterChange.emit({ field: filter.previous.field, value: null });
    }

    this.filterChange.emit(filter as Filter);
    this.filtersChange.emit(this.appliedFilters.getRawValue());
    LogHelper.debug('FilteredSearchComponent', `Updated filter: ${filter.field}`);
    this.removeExclusives(filter.field);
  }

  removeExclusives(fieldName: string) {
    const field = this.fieldDict[fieldName];
    if (field?.exclusiveWith?.length) {
      const removed = field.exclusiveWith.filter((e) => this.appliedFilters.getRawValue().find((f) => f.field === e));
      if (!removed.length) {
        return;
      }

      for (const filter of removed) {
        this.onRemoveFilter(filter);
      }
    }
  }

  onApplyFilter(
    field: string = null,
    value: FilterValueType = null,
    options: { emitFilterChange: boolean; emitFiltersChange: boolean } = {
      emitFilterChange: true,
      emitFiltersChange: false,
    },
  ): void {
    field = field ?? this.unappliedFilter.get('field').value;

    const valueControl = this.unappliedFilter.get('value');
    value = value ?? valueControl.value;

    if (!!field?.length && value !== null) {
      const existing = this.appliedFilters?.controls.find((f) => f.value.field === field);

      if (existing?.value?.value === value) {
        return;
      }

      if (existing) {
        existing.patchValue({ field, value: value as unknown });
      } else {
        let newValue: FilterFormValueType;

        if (typeof value === 'object' && !(value instanceof Date) && !Array.isArray(value)) {
          newValue = this.fb.group(value);
        } else if (Array.isArray(value)) {
          newValue = this.fb.array(value) as never;
        } else {
          newValue = this.fb.control(value) as never;
        }

        const group = this.fb.group({
          field,
          value: newValue,
        });

        this.appliedFilters?.push(group as never);
      }

      if (options?.emitFilterChange) {
        this.filterChange.emit({ field, value } as Filter);
      }

      if (options?.emitFiltersChange) {
        this.filtersChange.emit(this.appliedFilters.getRawValue());
      }

      LogHelper.debug('FilteredSearchComponent', `Applied filter: ${field}`);
      this.removeExclusives(field);
    }

    this.unappliedFilter = this.defaultFilterForm();
  }

  getValue(field: string, value?: FilterValueType): string | string[] {
    const variables = this.fieldDict[field]?.variables;
    if (!value && typeof value !== 'boolean') {
      return '';
    } else if (value instanceof Date) {
      return this.formatDate(value);
    } else if (this.isDateRange(value)) {
      return `${this.formatDate(value.start) ?? 'Not Specified'} - ${this.formatDate(value.end) ?? 'Not Specified'}`;
    } else {
      return this.formatString(value, variables);
    }
  }

  isHidden(field: string): boolean {
    return !!this.fieldDict[field]?.hide;
  }

  openCustomDateDialog(filterField: FilterField) {
    const dialogRef = this.dialog.open(CustomDateDialogComponent, {
      data: {
        filterField,
      },
    });

    dialogRef.afterClosed().subscribe((result: { field: string; value: unknown } | undefined) => {
      console.log('result', result);
      if (result) {
        this.onApplyFilter(result.field, result.value, { emitFilterChange: true, emitFiltersChange: true });
      }
    });
  }

  private formatString(
    value: string | number | boolean | string[] | null,
    variables?: FilterVariable[],
  ): string | string[] {
    if (typeof value === 'string') {
      let pipeSplit = value?.trim()?.split('||');
      if (pipeSplit?.length > 1) {
        pipeSplit = pipeSplit.map((pv) => {
          const variable = variables?.find((v) => v.value === pv);
          if (variable) {
            return `(${variable?.label})`;
          }

          return pv?.trim();
        });

        return pipeSplit;
      }

      let commaSplit = value?.toString()?.trim()?.split(',');

      if (commaSplit?.length > 1) {
        commaSplit = commaSplit.map((cv) => {
          const variable = variables?.find((v) => v.value === cv);
          if (variable) {
            return `(${variable?.label})`;
          }

          return cv?.trim();
        });

        return commaSplit;
      }

      if (variables?.length) {
        const variable = variables?.find((v) => v.value === value);
        if (variable) {
          return `(${variable?.label})`;
        }
      }
    }

    return value?.toString()?.trim();
  }

  private formatDate(date: Date): string {
    return date?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private isDateRange(value: any): value is DateRange {
    return (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      value && (value.start === null || value.start instanceof Date) && (value.end == null || value.end instanceof Date)
    );
  }
}
